import { useCoreService } from 'core/services/useCoreService';
import { createContext, useCallback, useEffect, useState } from 'react';
import { useDataFetcher } from 'shared/http';
import { Cuenta } from '../models/Cuenta';
import { SelectOption } from 'shared/form/models/SelectOption';
import { SearchCuentasRequest } from '../models/SearchCuentasRequests';

export const ALL_ACCOUNTS_SELECT_OPTION: SelectOption<Cuenta> = {
  label: 'Todas las cuentas',
  value: undefined,
};

interface ICuentaSelectorContext {
  cuentaList: SelectOption<Cuenta>[];
  cuentaUsuario: Cuenta;
  isLoading: boolean;
  isInitialLoading: boolean;
  searchAccounts: (searchAccountsRequest: SearchCuentasRequest) => void;
}

export const CuentaSelectorContext = createContext<ICuentaSelectorContext>({
  cuentaList: [],
  cuentaUsuario: null,
  isLoading: true,
  isInitialLoading: true,
  searchAccounts: () => null,
});

export const CuentaSelectorProvider = ({ children }) => {
  const [successfulFetches, setSuccessfulFetches] = useState(0);
  const [cuentaList, setCuentaListRequest] = useDataFetcher<SelectOption<Cuenta>[]>([], {
    onSuccess: useCallback(() => {
      setSuccessfulFetches((currentCount) => currentCount + 1);
    }, []),
  });
  const [cuentaUsuario, setCuentaUsuario] = useState<Cuenta>(null);
  const coreService = useCoreService();

  const searchAccounts = useCallback(
    (searchAccountsRequest: SearchCuentasRequest) => {
      const responseMapper = (lista: Cuenta[]): SelectOption<Cuenta>[] => {
        if (lista == null) {
          return [];
        }
        setCuentaUsuario(lista[0]);
        const result = lista.map((cuenta) => ({
          value: cuenta,
          label: cuenta.label,
        }));
        return result;
      };

      setCuentaListRequest(() =>
        coreService
          .getVisibleAccounts(searchAccountsRequest)
          .then((response) => responseMapper(response)),
      );
    },
    [coreService, setCuentaListRequest],
  );

  useEffect(() => searchAccounts({}), [searchAccounts]);

  const contextValue: ICuentaSelectorContext = {
    cuentaList: cuentaList.data,
    isLoading: cuentaList.isLoading,
    isInitialLoading: successfulFetches === 0 && cuentaList.isLoading,
    cuentaUsuario,
    searchAccounts,
  };

  return (
    <CuentaSelectorContext.Provider value={contextValue}>
      {children}
    </CuentaSelectorContext.Provider>
  );
};
